import React, { useEffect, useState, useCallback } from "react";
import {
   Button,
   Typography,
   Select,
   Option,
   Input
} from "@material-tailwind/react";
import { debounce } from 'lodash';
import { toast } from "react-toastify";
import { useLazyQuery } from "@apollo/client"
import { GETSTAFF } from "../../../GraphQl/queries";
import { text } from "stream/consumers";

interface DialogProps {
   isOpen: boolean;
   onClose: () => void;
   onChangeOwner: (id: string) => void;
};

interface CompanyStaff {
   id: string;
   id_number: string;
   name: string;
   staff_number: string;
}

export const ChangeOwnerDialog: React.FC<DialogProps> = ({ isOpen, onClose, onChangeOwner }) => {

   const [newOwnerId, setNewOwnerId] = useState("");
   const [searchText, setSearchText] = useState('');
   const [filteredResults, setFilteredResults] = useState<CompanyStaff[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [fetchStaffData, {
      data: staffData,
      loading: staffLoading,
      error: staffError
   }] = useLazyQuery(GETSTAFF);

   const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
         onClose();
      }
   };

   useEffect(() => {
      if (isOpen) {
         const company_id = localStorage?.getItem("company_id");
         fetchStaffData({ variables: { company_id: company_id } });
         setNewOwnerId("");
      }
   }, [isOpen, fetchStaffData]);

   useEffect(() => {
      if (staffError) {
         toast.error("Error fetching staff data");
      }
   }, [staffError]);

   const handleOwnerChange = () => {
      onChangeOwner(newOwnerId);
      localStorage.setItem("newOwnerId", newOwnerId);
      if (newOwnerId) {
         onClose();
      }
      else {
         toast.error("Owner ID cannot be empty");
      }
      clearSelection()
   }

   const clearSelection = () => {
      setSearchText("")
      setFilteredResults([])
   }

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;
      setSearchText(text);
      setIsLoading(true);
      // handleSearch(text);
   };

   const sortedStaff = Array.isArray(staffData?.company_staff)
      ? [...staffData.company_staff].sort((a: any, b: any) => a.name.localeCompare(b.name))
      : [];

   useEffect(() => {
      const sortStaff = Array.isArray(staffData?.company_staff)
         ? [...staffData.company_staff].sort((a: any, b: any) => a.name.localeCompare(b.name))
         : [];
      if (staffData && !searchText) {
         setFilteredResults(sortStaff);
         setIsLoading(false);
      } else {
         const lowercasedFilter = searchText.toLowerCase();
         const filtered = sortStaff.filter((staff: CompanyStaff) =>
            staff.name.toLowerCase().includes(lowercasedFilter) ||
            staff.staff_number.toLowerCase().includes(lowercasedFilter)
         );
         setFilteredResults(filtered);
         setIsLoading(false);
      }
   }, [searchText, staffData]);

   // const handleSearch = useCallback(
   //    debounce((text: string) => {
   //       console.log("Searching for:", text);
   //       if (!sortedStaff) return;

   //       if (text.trim() === '') {
   //          setFilteredResults(sortedStaff);
   //          setIsLoading(false);
   //          // return;
   //       }

   //       const lowercasedFilter = text.toLowerCase();
   //       const filtered = sortedStaff.filter((staff: any) =>
   //          staff.name?.toLowerCase().includes(lowercasedFilter) ||
   //          staff.staff_number?.toLowerCase().includes(lowercasedFilter)
   //       );

   //       const displayData = (searchText === '' || searchText === undefined || searchText === null) ? sortedStaff : filtered;

   //       setFilteredResults(displayData);
   //       setIsLoading(false);
   //    }, 300),
   //    [sortedStaff]
   // );

   return (
      <>
         {isOpen && (
            <div className="fixed inset-0 z-10 overflow-y-auto flex items-center justify-center bg-black bg-opacity-50"
               onClick={handleBackdropClick}>
               <div
                  className="bg-white rounded p-8 max-w-[500px] w-[40%] gap-y-6 flex flex-col items-center justify-center"
                  onClick={e => e.stopPropagation()}
               >
                  <Typography
                     variant="h4"
                     color="blue-gray"
                  >
                     Change Policy Owner
                  </Typography>
                  <p>Choose a New Owner</p>
                  <div className="w-full flex flex-col gap-4">
                     <div className="flex space-x-1 w-full">
                        <Input
                           crossOrigin="anonymous"
                           label="Search by name or staff number"
                           value={searchText}
                           onChange={handleChange}
                        />
                        {/* <button
                           // onClick={() => handleSearch(searchText)}
                           className="p-2 px-4 bg-black rounded-lg text-white"
                        >
                           Search
                        </button> */}
                     </div>
                     <div>
                        {sortedStaff.length > 0 &&
                           <div className="h-60 max-h-60 overflow-y-auto border-[1px] border-gray-300 rounded-md p-2  flex flex-col">
                              {isLoading && <p className="text-sm text-medium text-gray-600">Loading...</p>}
                              {!isLoading && sortedStaff.length === 0 && <p className="text-sm">No staff found</p>}
                              {
                                 filteredResults.map((staff: any) => {
                                    const displayName =
                                       'name' in staff ? staff.name : `${staff.first_name} ${staff.last_name}`;
                                    const staffNumber = 'staff_number' in staff ? staff.staff_number : '';

                                    return (
                                       <>
                                          <p
                                             key={staff.id}
                                             className="text-sm cursor-pointer focus:bg-gray-200 p-1 rounded hover:bg-gray-200"
                                             onClick={() => {
                                                setNewOwnerId(staff.id)
                                                setSearchText(staff.name)
                                             }}
                                          >
                                             {displayName} - {staffNumber}
                                          </p>
                                       </>
                                    );
                                 })}
                           </div>
                        }
                     </div>
                  </div>
                  <div className="flex flex-row items-center justify-end w-full">
                     <Button
                        className="rounded-md bg-red-600 py-2 px-4 border border-transparent text-center text-sm text-white transition-all shadow-md hover:shadow-lg focus:bg-red-700 focus:shadow-none active:bg-red-700 hover:bg-red-700 active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none ml-2" type="button"
                        onClick={() => {
                           onClose()
                           clearSelection()
                        }}>
                        Cancel
                     </Button>
                     <Button
                        className="rounded-md bg-green-600 py-2 px-4 border border-transparent text-center text-sm text-white transition-all shadow-md hover:shadow-lg focus:bg-green-700 focus:shadow-none active:bg-green-700 hover:bg-green-700 active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none ml-2" type="button"
                        onClick={handleOwnerChange}>
                        Confirm
                     </Button>
                  </div>
               </div>
            </div>
         )
         }
      </>
   );
};

export default ChangeOwnerDialog;